import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check } from "@phosphor-icons/react";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data?.items || []);
      setUnread(data?.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openNotif = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    if (n.proposal_id) nav(`/proposals/${n.proposal_id}`);
    load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read_all"); load(); } catch (e) { /* ignore */ }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" onClick={() => { setOpen(v => !v); if (!open) load(); }} data-testid="notif-bell-btn"
              className="relative p-2 rounded-sm hover:bg-slate-100 transition-colors" aria-label="Notifications">
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-amber-500" : "text-slate-700"}/>
        {unread > 0 && (
          <span data-testid="notif-unread-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-sm shadow-lg z-50" data-testid="notif-dropdown">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="label-micro">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all" className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1">
                <Check size={11}/> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <div className="px-3 py-6 text-center text-xs text-slate-500">You're all caught up.</div>}
            {items.map((n) => (
              <button key={n.id} type="button" onClick={() => openNotif(n)} data-testid={`notif-item-${n.id}`}
                      className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-blue-50 ${n.read ? "" : "bg-amber-50/50"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-amber-500 shrink-0"/>}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-800 leading-snug">{n.message}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
